import _ from 'lodash'
import { useTranslation } from 'react-i18next'
import { useCallback, useMemo } from 'react'
import { useDispatch } from 'react-redux'

import { useApi, useUmbracoProducts } from '../../hooks'
import { Button } from '../../components/Button'
import { Image } from '../../components/Image'
import { GetTypedSmallTextTable } from '../../components/SmallTextTable/SmallTextTable'
import { ClickableTableCell } from '../../components/ClickableTableCell'
import { DialogActions } from '../../redux/dialog'
import { ColumnType } from '../../components/Table'
import { Box } from '../../components/Box'
import { usePreferredProducts } from '../../hooks/usePreferredProducts'
import { useCountriesForPreferredProducts } from '../../hooks/useCountriesForPreferredProducts'

import { ProfileInfoBox } from './ProfileInfoBox'

type TablePreferredProducts = {
  country?: string
  countryCode?: string
  product?: string
  productCode?: string
}

const PreferredProductsTable = GetTypedSmallTextTable<TablePreferredProducts>()

const deleteIcon = <Image alt="delete" type="trashBlue" />

export const PreferredProductsProfileInfoBox = () => {
  const dispatch = useDispatch()

  const { t } = useTranslation(['account'])
  const tp = useCallback((key: string) => t(`account:Profile.${key}`), [t])

  const editButton = useMemo(
    () => (
      <Button variant="primary" size="middle" icon={<Image type="editPaperWhite" alt="edit" />} iconPlacement="right">
        {t('core:Edit')}
      </Button>
    ),
    [t]
  )

  const { countries, countriesLoading } = useCountriesForPreferredProducts()

  const { preferredProductsData, preferredProductsLoading, refreshPreferredProducts } = usePreferredProducts()

  const preferredProducts = preferredProductsData?.preferredProducts

  const allPreferredProductsSet = _.every(countries, c => _.some(preferredProducts, p => p.countryCode === c.code))

  const { request: deletePreferredProductRequest, loading: deletingPreferredProduct } = useApi()

  const { data: umbracoProducts, loading: umbracoProductsLoading } = useUmbracoProducts(
    _.compact(_.map(preferredProducts, 'productCode')),
    _.size(preferredProducts) === 0
  )

  const preferredProductsTableData = _.map(
    _.filter(countries, country => _.some(preferredProducts, ppd => ppd.countryCode === country.code)),
    country => {
      const preferredProduct = _.find(preferredProducts, ppd => ppd.countryCode === country.code)
      return {
        delete: deleteIcon,
        country: country.label,
        countryCode: country.code,
        product: _.find(umbracoProducts, up => up.bifCode === preferredProduct?.productCode)?.name,
        productCode: preferredProduct?.productCode,
        edit: editButton
      }
    }
  )

  const renderDelete = useMemo(
    // eslint-disable-next-line react/display-name
    () => (value: string, record: TablePreferredProducts) => {
      return (
        <ClickableTableCell
          onClick={async () => {
            await deletePreferredProductRequest({
              url: '/api/preferredProducts',
              method: 'DELETE',
              data: { countryCode: record.countryCode, productCode: record.productCode }
            })
            refreshPreferredProducts()
          }}
        >
          {value}
        </ClickableTableCell>
      )
    },
    [deletePreferredProductRequest, refreshPreferredProducts]
  )

  const renderEdit = useMemo(
    // eslint-disable-next-line react/display-name
    () => (value: string, record: TablePreferredProducts) => {
      return (
        <ClickableTableCell
          onClick={async () => {
            dispatch(
              DialogActions.showDialogWithData({
                dialog: 'UpdatePreferredProductDialog',
                data: {
                  mode: 'Edit',
                  preferredProducts,
                  refreshPreferredProducts,
                  focusedCountryCode: record.countryCode
                }
              })
            )
          }}
        >
          {value}
        </ClickableTableCell>
      )
    },
    [dispatch, preferredProducts, refreshPreferredProducts]
  )

  const tableColumns: ColumnType<TablePreferredProducts>[] = useMemo(
    () => [
      {
        id: 'delete',
        dataIndex: 'delete',
        width: '24px',
        visibilityType: 'non-selectable',
        render: renderDelete
      },
      {
        id: 'country',
        dataIndex: 'country',
        title: tp('Country')
      },
      {
        id: 'product',
        dataIndex: 'product',
        title: tp('PreferredProduct')
      },
      {
        id: 'edit',
        dataIndex: 'edit',
        visibilityType: 'non-selectable',
        width: '0px',
        render: renderEdit
      }
    ],
    [renderDelete, tp, renderEdit]
  )

  const renderTable = useCallback(
    () => (
      <Box mt={[0, 2, 2]} mr={[0, 4, 4]}>
        <PreferredProductsTable
          rowKey={(row: TablePreferredProducts) => _.join(row.countryCode, row.productCode)}
          dataSource={preferredProductsTableData}
          loading={preferredProductsLoading || umbracoProductsLoading || deletingPreferredProduct || countriesLoading}
          columns={tableColumns}
        />
      </Box>
    ),
    [
      deletingPreferredProduct,
      preferredProductsLoading,
      preferredProductsTableData,
      tableColumns,
      umbracoProductsLoading,
      countriesLoading
    ]
  )

  return (
    <ProfileInfoBox
      rows={[
        {
          cells: [
            {
              label: tp('PreferredProducts'),
              value: tp('PreferredProductsDescription')
            }
          ],
          action: {
            text: t('core:Add'),
            iconName: 'plusCircleEmptyWhite',
            disabled: !preferredProducts || allPreferredProductsSet,
            loading: preferredProductsLoading || umbracoProductsLoading || deletingPreferredProduct || countriesLoading,
            onClick: () =>
              dispatch(
                DialogActions.showDialogWithData({
                  dialog: 'UpdatePreferredProductDialog',
                  data: {
                    mode: 'Add',
                    preferredProducts,
                    refreshPreferredProducts
                  }
                })
              )
          },
          content:
            preferredProductsLoading ||
            umbracoProductsLoading ||
            deletingPreferredProduct ||
            countriesLoading ||
            _.size(preferredProductsTableData) > 0
              ? renderTable
              : undefined
        }
      ]}
      image={<Image alt="file" type="fileTextWhite" />}
    />
  )
}
